import React, { useState, useEffect } from "react";
import {
  Check,
  CreditCard,
  Shield,
  Zap,
  History,
  Crown,
  FileText,
  Loader,
  Users,
  Scissors,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Info,
  Clock,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { collection, getDocs, getCountFromServer } from "firebase/firestore";
import { db } from "../../config/firebase";
import Modal from "../../components/common/Modal";
import { useNavigate } from "react-router-dom";
// IMPORTANTE: Importar a função de serviço correta
import { getSubscriptionPlans } from "../../services/firestoreService";

// Função auxiliar para calcular dias restantes
const getTrialDaysLeft = (trialExpiresAt) => {
  if (!trialExpiresAt) return 0;
  const expiryDate = trialExpiresAt.toDate
    ? trialExpiresAt.toDate()
    : new Date(trialExpiresAt);
  const today = new Date();
  const diffTime = expiryDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const SubscriptionPage = () => {
  const { companyInfo } = useAuth();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado para o Gatekeeper
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState({
    message: "",
    current: 0,
    limit: 0,
    action: "",
    warnings: [],
  });

  const [usage, setUsage] = useState({
    professionals: 0,
    services: 0,
  });

  const subscriptionData = companyInfo?.subscription || {};
  const currentPlanId = subscriptionData.planId || "trial";

  const currentPlanObj = plans.find((p) => p.id === currentPlanId);
  const planNameDisplay = currentPlanObj
    ? currentPlanObj.name
    : subscriptionData.planName || "Plano Personalizado";

  const limits = subscriptionData.limits || {};

  // Cálculos do Trial para o Banner
  const isTrial = subscriptionData.status === "trial";
  const daysLeft = getTrialDaysLeft(subscriptionData.trialExpiresAt);
  const showTrialBanner = isTrial && daysLeft <= 5 && daysLeft >= 0;
  const isUrgent = daysLeft <= 2;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // CORREÇÃO AQUI: Passamos 'true' para filtrar apenas planos VISÍVEIS
        const plansData = await getSubscriptionPlans(true);
        setPlans(plansData);

        if (companyInfo?.id) {
          const profsRef = collection(
            db,
            "companies",
            companyInfo.id,
            "professionals"
          );
          const profsSnap = await getDocs(profsRef);

          const activeProfsCount = profsSnap.docs.filter(
            (doc) => doc.data().active !== false
          ).length;

          const servicesRef = collection(
            db,
            "companies",
            companyInfo.id,
            "services"
          );
          const servicesSnap = await getCountFromServer(servicesRef);

          setUsage({
            professionals: activeProfsCount,
            services: servicesSnap.data().count,
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyInfo]);

  const UsageBar = ({ label, current, max, icon: Icon }) => {
    const isUnlimited = max === 0 || max === undefined;
    const percentage = isUnlimited ? 100 : Math.min((current / max) * 100, 100);

    let colorClass = "bg-brand-blue";
    if (!isUnlimited) {
      if (percentage >= 90) colorClass = "bg-red-500";
      else if (percentage >= 70) colorClass = "bg-yellow-500";
      else colorClass = "bg-green-500";
    }

    return (
      <div className="mb-4 last:mb-0">
        <div className="flex justify-between items-center mb-1 text-sm">
          <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium text-xs sm:text-sm">
            <Icon className="w-4 h-4 text-slate-400" />
            {label}
          </span>
          <span className="text-slate-900 dark:text-white font-bold text-xs sm:text-sm">
            {current}{" "}
            <span className="text-slate-400 text-[10px] sm:text-xs font-normal">
              / {isUnlimited ? "∞" : max}
            </span>
          </span>
        </div>
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${colorClass}`}
            style={{ width: `${isUnlimited ? 100 : percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const getPlanStyle = (index, isRecommended) => {
    const styles = [
      {
        icon: <Zap className="w-6 h-6 text-blue-500" />,
        color:
          "border-slate-200 dark:border-slate-700 hover:border-blue-500/50",
        buttonColor:
          "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700",
      },
      {
        icon: <Crown className="w-6 h-6 text-yellow-500" />,
        color:
          "border-blue-500 shadow-lg shadow-blue-500/10 transform md:scale-105 z-10 ring-1 ring-blue-500",
        buttonColor:
          "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/30",
      },
      {
        icon: <Shield className="w-6 h-6 text-purple-500" />,
        color:
          "border-slate-200 dark:border-slate-700 hover:border-purple-500/50",
        buttonColor: "bg-purple-600 hover:bg-purple-700 text-white",
      },
    ];
    // Se o plano for "Starter" (geralmente o primeiro), força o estilo básico
    // Se for "Recommended", usa o estilo de destaque
    // Senão, usa o estilo enterprise/padrão
    if (isRecommended) return styles[1];
    return styles[index % 3] || styles[2];
  };

  const handleSubscribe = (targetPlan) => {
    if (!targetPlan.asaasUrl) {
      alert("Link de pagamento indisponível. Contate o suporte.");
      return;
    }

    const targetLimit = targetPlan.limits?.maxProfessionals;
    const isUnlimited = targetLimit === 0 || targetLimit === undefined;

    const downgradeWarnings = [];
    if (targetPlan.name === "Starter") {
      downgradeWarnings.push(
        "O módulo de Estoque ficará em modo somente leitura."
      );
      downgradeWarnings.push(
        "O Financeiro Avançado (Caixa) ficará indisponível."
      );
      downgradeWarnings.push("Você perderá o acesso a Relatórios de Comissão.");
    }

    if (!isUnlimited && usage.professionals > targetLimit) {
      setBlockReason({
        message: `O plano ${targetPlan.name} permite apenas ${targetLimit} profissionais.`,
        current: usage.professionals,
        limit: targetLimit,
        action: `Você precisa desativar ${
          usage.professionals - targetLimit
        } profissionais antes de mudar de plano.`,
        warnings: downgradeWarnings,
      });
      setBlockModalOpen(true);
      return;
    }

    window.open(targetPlan.asaasUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <Loader className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 bg-light-bg dark:bg-dark-bg min-h-full transition-colors duration-200">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-brand-blue" />
            Minha Assinatura
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Gerencie seu plano e acompanhe o uso dos recursos.
          </p>
        </div>
      </div>

      {/* --- BANNER DE ALERTA DE TRIAL --- */}
      {showTrialBanner && (
        <div
          className={`p-4 sm:p-5 rounded-xl border flex flex-col sm:flex-row items-start gap-4 animate-in slide-in-from-top-4 duration-500 ${
            isUrgent
              ? "bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800/30"
              : "bg-orange-50 border-orange-100 dark:bg-orange-900/20 dark:border-orange-800/30"
          }`}
        >
          <div
            className={`p-2.5 rounded-lg shrink-0 ${
              isUrgent
                ? "bg-red-100 text-red-600 dark:bg-red-800/40 dark:text-red-400"
                : "bg-orange-100 text-orange-600 dark:bg-orange-800/40 dark:text-orange-400"
            }`}
          >
            <Clock className="w-6 h-6" />
          </div>

          <div className="flex-1">
            <h3
              className={`font-bold text-lg mb-1 ${
                isUrgent
                  ? "text-red-800 dark:text-red-300"
                  : "text-orange-800 dark:text-orange-300"
              }`}
            >
              {daysLeft <= 0
                ? "Seu período de teste acaba hoje!"
                : daysLeft === 1
                ? "É o seu último dia de teste!"
                : `Restam apenas ${daysLeft} dias de teste`}
            </h3>
            <p
              className={`text-sm leading-relaxed ${
                isUrgent
                  ? "text-red-700 dark:text-red-400"
                  : "text-orange-700 dark:text-orange-400"
              }`}
            >
              Aproveite para garantir a continuidade do seu acesso. Ao assinar
              agora, você mantém todos os dados cadastrados e desbloqueia o
              potencial total do NavalhaSystem sem interrupções.
            </p>
          </div>

          <button
            onClick={() => {
              const plansSection = document.getElementById("plans-options");
              if (plansSection)
                plansSection.scrollIntoView({ behavior: "smooth" });
            }}
            className={`whitespace-nowrap px-5 py-2.5 rounded-lg font-bold text-sm shadow-md transition-all transform active:scale-95 hover:shadow-lg ${
              isUrgent
                ? "bg-red-600 text-white hover:bg-red-700 shadow-red-500/20"
                : "bg-orange-500 text-white hover:bg-orange-600 shadow-orange-500/20"
            }`}
          >
            Escolher Plano
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CARTÃO DE STATUS */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-card border border-slate-200 dark:border-white/10 rounded-2xl p-5 sm:p-6 relative overflow-hidden shadow-sm flex flex-col justify-between">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-blue/5 dark:bg-brand-blue/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
              <span
                className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold border ${
                  subscriptionData.status === "active"
                    ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30"
                    : "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/30"
                }`}
              >
                {subscriptionData.status === "trial"
                  ? "PERÍODO DE TESTES"
                  : "ASSINATURA ATIVA"}
              </span>
              {subscriptionData.trialExpiresAt && isTrial && (
                <span
                  className={`text-xs sm:text-sm flex items-center gap-1 font-medium ${
                    isUrgent
                      ? "text-red-500 animate-pulse"
                      : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  <History className="w-3 h-3 sm:w-4 sm:h-4" />
                  {isUrgent ? "Expira em breve!" : "Contagem regressiva"}
                </span>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-1">
              Plano Atual:{" "}
              <span className="text-brand-blue">{planNameDisplay}</span>
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm max-w-xl">
              Seu pacote de recursos está ativo. Confira ao lado o consumo atual
              da sua franquia.
            </p>
          </div>
          <div className="mt-6 flex items-center gap-4 relative z-10">
            <button className="w-full sm:w-auto px-6 py-2.5 rounded-lg font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 transition-colors text-sm">
              Falar com Suporte
            </button>
          </div>
        </div>

        {/* CARTÃO DE CONSUMO */}
        <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-white/10 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <TrendingUp className="w-5 h-5 text-brand-blue" />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-lg">
              Consumo
            </h3>
          </div>
          <UsageBar
            label="Profissionais Ativos"
            icon={Users}
            current={usage.professionals}
            max={limits.maxProfessionals}
          />
          <div className="h-4"></div>
          <UsageBar
            label="Serviços"
            icon={Scissors}
            current={usage.services}
            max={limits.maxServices}
          />
        </div>
      </div>

      {/* LISTA DE PLANOS */}
      <div id="plans-options">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mt-8 mb-6">
          Opções de Upgrade
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const style = getPlanStyle(index, plan.recommended);
            const isCurrentPlan = currentPlanId === plan.id;
            return (
              <div
                key={plan.id}
                className={`relative bg-white dark:bg-dark-card rounded-2xl p-5 sm:p-6 border transition-all duration-300 flex flex-col ${style.color}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-[10px] sm:text-xs font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
                    MAIS ESCOLHIDO
                  </div>
                )}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 transition-colors border border-slate-100 dark:border-slate-700">
                      {style.icon}
                    </div>
                    {isCurrentPlan && (
                      <span className="text-[10px] sm:text-xs font-bold text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded">
                        PLANO ATUAL
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      R$
                    </span>
                    <span className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
                      {plan.price}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      /{plan.period || "mês"}
                    </span>
                  </div>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-8 flex-1">
                  <li className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                    <Check className="w-5 h-5 text-green-500 shrink-0" />
                    <span>
                      Até{" "}
                      <strong>{plan.limits?.maxProfessionals || "∞"}</strong>{" "}
                      Barbeiros
                    </span>
                  </li>
                  {plan.features &&
                    plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300"
                      >
                        <Check className="w-5 h-5 text-green-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={isCurrentPlan}
                  className={`w-full py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${style.buttonColor}`}
                >
                  {isCurrentPlan ? "Seu Plano Atual" : "Assinar Agora"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* HISTÓRICO DE FATURAS */}
      <div className="mt-8">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
          Histórico de Faturas
        </h3>
        <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-white/10 rounded-xl overflow-x-auto p-8 text-center text-slate-500 dark:text-slate-400 flex flex-col items-center shadow-sm">
          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
            <FileText className="w-6 h-6 text-slate-400" />
          </div>
          <p>Nenhuma fatura gerada ainda.</p>
        </div>
      </div>

      {/* MODAL GATEKEEPER */}
      <Modal
        isOpen={blockModalOpen}
        onClose={() => setBlockModalOpen(false)}
        title="Ajuste Necessário"
      >
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>

          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            Não é possível mudar de plano agora
          </h3>

          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
            {blockReason.message}
            <br />
            Você possui <strong>{blockReason.current}</strong> ativos, mas o
            limite é <strong>{blockReason.limit}</strong>.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 text-left">
            <p className="text-sm font-semibold text-slate-700 dark:text-white mb-2 flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" /> Ação Necessária:
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {blockReason.action}
            </p>
          </div>

          {blockReason.warnings && blockReason.warnings.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/10 p-4 rounded-lg border border-yellow-100 dark:border-yellow-900/30 text-left">
              <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-500 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" /> Importante:
              </p>
              <ul className="list-disc list-inside space-y-1">
                {blockReason.warnings.map((warning, index) => (
                  <li
                    key={index}
                    className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-400"
                  >
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              onClick={() => setBlockModalOpen(false)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => navigate("/dashboard/equipe")}
              className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Ir para Equipe <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPage;
